import { useEffect, useMemo, useState } from 'react';

import { Field } from 'formik';

import DynamicPropertyMain from '../DynamicProperty/DynamicPropertyMain';
import { TabVisibilityConfig } from '../../config/tabVisibilityConfig';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import projectService from '../../service/project.service';
import { FormErrorUI, FormLabelUI, InputUI, TabsUI, FileTabUI, DeleteDialogUI, CheckboxUI } from '../Interface';
import ProjectCommunication from './ProjectCommunication';
import ProjectContact from './ProjectContact';
import ProjectResourceOneTable from './Resources/ResourceOneTable';
import ProjectResourceTwoTable from './Resources/ResourceTwoTable';
import ProjectResourceThreeTable from './Resources/ResourceThreeTable';
import ProjectResourceFourTable from './Resources/ResourceFourTable';
import ProjectResourceFiveTable from './Resources/ResourceFiveTable';
import ProjectHistory from './ProjectHistory/ProjectHistory';
import CheckLists from '../CheckList/CheckListTab/CheckLists';
import ProjectTaskList from '../TaskList/TaskListTab/ProjectTaskList';
import useSaveView from '../../hooks/useSaveView';
import useView from '../../hooks/useView';
import ExcelImportTab from '../Interface/ExcelImportTab';
import jokerService from '../../service/joker.service';
import { JokerTypes } from '../../config/joker';
import JokerTab from '../Interface/Joker/JokerTab';
import ProjectPriceTable from './ProjectPriceTable';
import Comments from '../Comment/Comments';
import ProjectEmployeeTable from './ProjectEmployeeTable';

const PropertyGroups = (props) => {
  const { propertyGroups, groupState, user } = props;
  const { group, setGroup } = groupState;
  const saveView = useSaveView(user);
  const savedGroups = useView('projectPage', user);

  const handleCheckboxChange = (id) => {
    setGroup((prevGroup) => {
      const isSelected = prevGroup.id.includes(id);
      const updatedIds = isSelected ? prevGroup.id.filter((groupId) => groupId !== id) : [...prevGroup.id, id];

      const newGroup = { ...prevGroup, id: updatedIds };

      saveView('projectPage', { dynGroup: updatedIds });

      return newGroup;
    });
  };

  useEffect(() => {
    if (Object.keys(savedGroups).length > 0) {
      setGroup((prevGroup) => ({
        ...prevGroup,
        id: savedGroups.dynGroup,
      }));
    }
  }, [savedGroups]);

  return (
    <div className="flex -mt-5 flex-wrap">
      {propertyGroups.length > 0 &&
        propertyGroups.map((groupItem) => (
          <div key={groupItem.id}>
            <CheckboxUI
              label={groupItem.name}
              checked={group.id.includes(groupItem.id)}
              onChange={() => handleCheckboxChange(groupItem.id)}
            />
          </div>
        ))}
    </div>
  );
};

const DescriptionPanel = ({ disabled, errors, values }) => {
  return (
    <div>
      <div className="col-span-full relative">
        <div className="mt-2">
          <Field
            type="text"
            name="projectDescription"
            as={InputUI}
            className={`${disabled ? 'rounded-md' : ''} resize-none h-36, p-2`}
            fullWidth
            label={<FormLabelUI text="Leírás" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.projectDescription} />}
            disabled={disabled}
            InputLabelProps={{ shrink: values.projectDescription !== '' }}
            multiline
            rows={10}
          />
        </div>
      </div>
    </div>
  );
};

const ProjectTabs = (props) => {
  const { user, selectedValues, setSelectedValues, disabled, setRefetchTable, addField } = props;
  const [activeTab, setActiveTab] = useState(0);
  const [removeElement, setRemoveElement] = useState(null);
  const [deleteDialog, setDeletDialog] = useState(false);
  const [jokerGroups, setJokerGroups] = useState([]);

  useEffect(() => {
    getJokerGroups();
  }, []);

  const handleUpload = (files) => {
    if (!selectedValues.projectId) {
      return addNotification({
        content: 'Nincs kiválasztott elem!',
        type: NotificationType.ERROR,
      });
    }

    const uploadPromises = files.map((file) =>
      projectService.uploadFile(file, selectedValues.projectId, user.userId).then((data) => {
        setSelectedValues((prev) => ({ ...prev, projectAttachment: data }));
      })
    );

    Promise.all(uploadPromises)
      .then(() => {
        setRefetchTable(true);
      })
      .catch((error) => {
        console.error('Fájl feltöltési hiba:', error);
      });
  };

  const handleDelete = (data) => {
    setRemoveElement(data.id);
    setDeletDialog(true);
  };

  const hideDeleteDialog = () => {
    setDeletDialog(false);
    setRefetchTable(null);
  };
  const handleRemoveElement = (data) => {
    projectService.deleteFile(removeElement, user.userId).then((data) => {
      setSelectedValues((prev) => ({ ...prev, projectAttachment: data }));
      hideDeleteDialog();
    });
  };

  const getJokerGroups = async () => {
    jokerService.getGroups(JokerTypes.PROJECT).then((data) => {
      setJokerGroups(data);
    });
  };

  const tabs = [
    {
      id: 0,
      name: 'Tulajdonságok',
      content: (
        <div>
          <PropertyGroups {...props} />
          <DynamicPropertyMain {...props} />
        </div>
      ),
      key: 'PROPERTIES',
    },
    {
      id: 1,
      name: 'Checklista',
      content: <CheckLists {...props} />,
      key: 'CHECKLIST',
    },
    {
      id: 2,
      name: 'Feladat lista',
      content: <ProjectTaskList {...props} />,
      key: 'TASKLIST',
    },
    {
      id: 3,
      name: 'Pótmunka',
      content: <ProjectTaskList {...props} isExtraWorkType={true} />,
      key: 'EXTRAWORK',
    },
    {
      id: 4,
      name: 'Megjegyzés',
      content: <DescriptionPanel {...props} />,
      key: 'DESCRIPTION',
    },
    {
      id: 5,
      name: 'Hozzászólások',
      content: (
        <Comments
          rows={selectedValues.comments ?? []}
          lineId={selectedValues.projectId}
          subMenuUrl="Project"
          setRefetchTable={setRefetchTable}
          disabled={disabled || !addField}
        />
      ),
      key: 'COMMENT',
    },
    {
      id: 6,
      name: 'Projekt tagok',
      content: <ProjectEmployeeTable {...props} />,
      key: 'PROJECT_EMPLOYEE',
    },
    {
      id: 7,
      name: props.submenuNames[0],
      content: <ProjectResourceOneTable {...props} />,
      key: 'RESOURCE_ONE',
    },
    {
      id: 8,
      name: props.submenuNames[1],
      content: <ProjectResourceTwoTable {...props} />,
      key: 'RESOURCE_TWO',
    },
    {
      id: 9,
      name: props.submenuNames[2],
      content: <ProjectResourceThreeTable {...props} />,
      key: 'RESOURCE_THREE',
    },
    {
      id: 10,
      name: 'Kapcsolattartó',
      content: <ProjectContact {...props} />,
      key: 'CONTACT',
    },
    {
      id: 11,
      name: 'Csatolmányok',
      content: (
        <FileTabUI
          disabled={disabled || !selectedValues.projectId}
          savedFiles={[
            ...(props.selectedValues.projectAttachment ?? []),
            ...(props.selectedValues.taskLists ?? []).flatMap((taskList) => taskList.taskListAttachment ?? []),
          ]}
          handleUpload={handleUpload}
          handleDelete={handleDelete}
        />
      ),
      key: 'ATTACHMENT',
    },
    {
      id: 12,
      name: 'Kommunikáció',
      content: <ProjectCommunication {...props} />,
      key: 'COMMUNICATION',
    },
    {
      id: 13,
      name: props.submenuNames[3],
      content: <ProjectResourceFourTable {...props} />,
      key: 'RESOURCE_FOUR',
    },
    {
      id: 14,
      name: props.submenuNames[4],
      content: <ProjectResourceFiveTable {...props} />,
      key: 'RESOURCE_FIVE',
    },
    {
      id: 15,
      name: 'Projekt történet',
      content: <ProjectHistory {...props} />,
      key: 'PROJECT_HISTORY',
    },
    {
      id: 16,
      name: 'Adat import',
      content: <ExcelImportTab {...props} />,
      key: 'PROJECT_IMPORT',
    },
    {
      id: 17,
      name: 'Szerződött óradíjak',
      content: <ProjectPriceTable {...props} />,
      key: 'PROJECT_PRICE',
    },
    ...((jokerGroups.length > 0 &&
      jokerGroups
        .filter((group) => group.jokerFields.length > 0)
        .map((group, index) => ({
          id: index + 18,
          name: group.name,
          key: `JOKER_${group.id}`,
          content: <JokerTab fields={group.jokerFields} key={index} {...props} />,
        }))) ||
      []),
  ];

  const visibleTabs = useMemo(() => {
    const tabVisibilityMap = props.params.PROJECT_TAB_FIELDS
      ? JSON.parse(props.params.PROJECT_TAB_FIELDS).reduce((acc, field) => {
          const [key, value] = Object.entries(field)[0];
          acc[key] = value;
          return acc;
        }, {})
      : {};

    return tabs.filter((tab) => {
      if (!props.canEdit && props.infoPanel && TabVisibilityConfig[tab.key]?.hideWhenCanEditFalse) {
        return false;
      }
      if (tab.key === 'PROPERTIES') {
        return true;
      }
      if (!props.params.PROJECT_TAB_FIELDS) {
        return true;
      }
      if (tab.key.startsWith('RESOURCE_')) {
        const resourceIndex = parseInt(tab.key.split('_')[1]) - 1;
        return (
          (!tabVisibilityMap[tab.key] || tabVisibilityMap[tab.key] === 'Y') &&
          props.submenuNames[resourceIndex] !== null
        );
      }

      return !tabVisibilityMap[tab.key] || tabVisibilityMap[tab.key] === 'Y';
    });
  }, [props.params.PROJECT_TAB_FIELDS, tabs, props.submenuNames]);

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  return (
    <div className="my-4 mt-6">
      <div className="px-3">
        <TabsUI activeTab={activeTab} handleTabClick={handleTabClick}>
          {visibleTabs.map((tab, index) => (
            <div key={index} name={tab.name}>
              {tab.content}
            </div>
          ))}
        </TabsUI>
      </div>
      <DeleteDialogUI handleRemoveElement={handleRemoveElement} show={deleteDialog} onHide={() => hideDeleteDialog()} />
    </div>
  );
};

export default ProjectTabs;
